import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  AssessmentType,
  GetAssessmentQuery,
  GetAssessmentQueryVariables,
  GetChatSessionsForAssessmentQueryQuery,
  GetChatSessionsForAssessmentQueryQueryVariables,
  SuggestedAnswerState,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import getChatSessionsForAssessmentQuery from "@apps/assessments/graphql/get-chat-sessions-for-assessment-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Layout from "@components/layout/layout";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import getAssessmentQuery from "../../graphql/get-assessment-query";
import { getAssessmentNextRoleVisibility } from "../../helpers";
import AssessmentLayoutSidebar from "../assessment-layout-sidebar";
import { getAssessmentPageContext } from "../assessment-page-context";
import { assessmentContextSidebarVar } from "../context-sidebar/context-sidebar-context";
import { AssessmentAnswer } from "../questions/types";
import {
  ReviewForAiTool,
  canCompleteInAiTool,
} from "../review-ai-tool/review-ai-tool";
import AssessmentContent from "./assessment-content";

const getAnswersFromAssessment = (
  assessment: GetAssessmentQuery["assessment"],
): AssessmentAnswer[] => {
  const answersEdges = compact(assertNonNull(assessment).answers);
  return answersEdges.map((a) => ({
    questionId: a.questionId,
    integerAnswer:
      a.__typename === "RangeAssessmentAnswerNode" ||
      a.__typename === "NpsAssessmentAnswerNode" ||
      a.__typename === "ResponsibilityAssessmentAnswerNode" ||
      a.__typename === "CompetencyAssessmentAnswerNode" ||
      a.__typename === "IndividualGoalAssessmentAnswerNode" ||
      a.__typename === "OverallGoalAssessmentAnswerNode" ||
      a.__typename === "IndividualRecognitionCoreValueAssessmentAnswerNode" ||
      a.__typename === "OverallRecognitionCoreValueAssessmentAnswerNode" ||
      a.__typename === "CompetencyCriteriaAssessmentAnswerNode" ||
      a.__typename === "TalentIndicatorAssessmentAnswerNode"
        ? a.integerAnswer
        : null,
    textAnswer:
      a.__typename === "TextAssessmentAnswerNode" ? a.textAnswer : null,
    choices:
      a.__typename === "MultiChoiceAssessmentAnswerNode"
        ? [...a.choices]
        : null,
    comment: a.comment,
    isLocked: a.isLocked,
  }));
};

const Assessment = ({ isInSidebar }: { isInSidebar?: boolean }) => {
  const location = useLocation<TFLocationState>();
  const [nextRoleVisibilityOverrides, setNextRoleVisibility] = useState<
    Record<number, boolean>
  >({});
  const [proposedAnswers, setProposedAnswers] = useState<
    AssessmentAnswer[] | null
  >(null);
  const { assessmentId: assessmentIdParam } = useParams<{
    assessmentId: string;
  }>();
  const assessmentId = parseInt(assessmentIdParam);
  const organization = currentOrganizationVar();
  const label = useLabel();
  const currentUser = currentUserVar();

  const { data: myAssessmentsData, loading: isLoadingAssessment } = useQuery<
    GetAssessmentQuery,
    GetAssessmentQueryVariables
  >(getAssessmentQuery, {
    fetchPolicy: "no-cache",
    variables: {
      assessmentId,
      organizationId: organization?.id,
    },
    onCompleted: (response) => {
      setProposedAnswers(getAnswersFromAssessment(response.assessment));
    },
  });

  const assessment = useMemo(
    () => (myAssessmentsData ? myAssessmentsData.assessment : null),
    [myAssessmentsData],
  );

  const aiToolReview: ReviewForAiTool | null =
    assessment &&
    canCompleteInAiTool({
      assessmentType: assessment.template.assessmentType,
      state: assessment.state,
      canUpdate: !!assessment.canUpdate?.permission,
      isResponder: assessment.responder?.id === currentUser.id,
    })
      ? {
          assessmentId: assessment.id,
          isSelf:
            !!assessment.target &&
            assessment.target.id === assessment.responder?.id,
        }
      : null;

  const nextRoleVisibility = useMemo(
    () =>
      getAssessmentNextRoleVisibility(
        assessment,
        proposedAnswers || [],
        nextRoleVisibilityOverrides,
      ),
    [assessment, proposedAnswers, nextRoleVisibilityOverrides],
  );

  const pageContextMessage = useCallback(
    () =>
      organization.featureFlags.askAi
        ? getAssessmentPageContext(
            assessment,
            currentUser.id,
            label("review"),
            nextRoleVisibility,
          )
        : undefined,
    [
      assessment,
      currentUser.id,
      organization.featureFlags.askAi,
      label,
      nextRoleVisibility,
    ],
  );

  const isManager = assessment?.target?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  const isEngagementSurvey = useMemo(
    () =>
      assessment?.template.assessmentType === AssessmentType.EngagementSurvey,
    [assessment],
  );

  const shouldLoadContextGenerationState =
    !!assessment &&
    !isInSidebar &&
    !isEngagementSurvey &&
    organization.featureFlags.reviewContextGeneration &&
    organization.featureFlags.askAi;

  // preload chat sessions and suggested-answer readiness for assessment context buttons
  const {
    data: chatSessionsForAssessmentData,
    error: chatSessionsForAssessmentError,
    loading: isLoadingContextGenerationState,
    startPolling: startPollingContextGenerationState,
    stopPolling: stopPollingContextGenerationState,
  } = useQuery<
    GetChatSessionsForAssessmentQueryQuery,
    GetChatSessionsForAssessmentQueryQueryVariables
  >(getChatSessionsForAssessmentQuery, {
    variables: { assessmentId },
    skip: !shouldLoadContextGenerationState,
  });

  useEffect(() => {
    if (
      shouldLoadContextGenerationState &&
      !chatSessionsForAssessmentError &&
      !isLoadingContextGenerationState &&
      chatSessionsForAssessmentData?.assessment?.suggestedAnswersState !==
        SuggestedAnswerState.Generated
    ) {
      startPollingContextGenerationState(5000);
      return () => stopPollingContextGenerationState();
    }

    stopPollingContextGenerationState();
  }, [
    chatSessionsForAssessmentError,
    chatSessionsForAssessmentData?.assessment?.suggestedAnswersState,
    isLoadingContextGenerationState,
    shouldLoadContextGenerationState,
    startPollingContextGenerationState,
    stopPollingContextGenerationState,
  ]);

  const target = useMemo(
    () =>
      !isEngagementSurvey && assessment
        ? assertNonNull(assessment.target)
        : null,
    [assessment, isEngagementSurvey],
  );

  const relatedMeetings = assessment?.relatedMeetings
    ? assertEdgesNonNull(assessment?.relatedMeetings)
    : [];

  useEffect(() => {
    setProposedAnswers(null);
    setNextRoleVisibility({});
  }, [assessmentId]);

  const isSelfAssessment = useMemo(
    // An anonymous survey has no subject and may hide its writer: two missing ids are not a self review.
    () =>
      !!assessment?.target && assessment.target.id === assessment.responder?.id,
    [assessment],
  );

  const path = useMemo(() => {
    if (isEngagementSurvey || location.pathname.includes("/survey")) {
      return `/survey`;
    }
    return `/assessments`;
  }, [location.pathname, isEngagementSurvey]);

  const assessmentLabel = useMemo(() => {
    if (isEngagementSurvey) {
      return "Survey";
    }
    return "review";
  }, [isEngagementSurvey]);

  useDocumentTitle(
    label(assessmentLabel, { capitalize: true, pluralize: true }),
  );

  // Reset the question ID when the assessment changes
  useEffect(() => {
    assessmentContextSidebarVar(null);

    // Reset on unmount so we don't persist the ask ai context generator across pages
    return () => {
      assessmentContextSidebarVar(null);
    };
  }, [assessment?.id]);

  return (
    <Layout className="bg-white">
      {!isInSidebar && (
        <Layout.Header
          breadcrumbs={compact([
            {
              title: label(assessmentLabel, {
                titleCase: true,
                pluralize: true,
              }),
              url: path,
            },
            // Reviews link back to their program/review. Surveys keep the
            // program title on the final crumb instead (they have no subject).
            !isEngagementSurvey &&
              assessment?.complianceProgram && {
                title: assessment.complianceProgram.title || "Untitled",
                url: `${path}/program/${assessment.complianceProgram.id}`,
              },
            assessment && {
              title: `${getAssessmentTypeLabel(
                assertNonNull(assessment.template.assessmentType),
                isSelfAssessment,
              )} ${
                isEngagementSurvey
                  ? ""
                  : label(assessmentLabel, { titleCase: true })
              }: ${
                isEngagementSurvey
                  ? assessment.complianceProgram?.title || "Untitled"
                  : target?.name || "Untitled"
              }`,
              url: `${path}/assessment/${assessmentId}`,
            },
          ])}
          sidebarExpandedUiPreferenceKey={
            isEngagementSurvey ? undefined : "assessmentSidebarExpanded"
          }
          className="bg-white"
        />
      )}
      <Layout.Container loading={isLoadingAssessment} noHeader={isInSidebar}>
        <Layout.Main
          width={isInSidebar ? "sidebar" : "md"}
          className="print:px-4"
          sidebarExpandedUiPreferenceKey={
            isInSidebar
              ? undefined
              : isEngagementSurvey
                ? undefined
                : "assessmentSidebarExpanded"
          }
        >
          {assessment && proposedAnswers !== null && (
            <div aria-label="Assessments > performance assessment form">
              <AssessmentContent
                myAssessmentsData={myAssessmentsData}
                isLoadingAssessment={isLoadingAssessment}
                proposedAnswers={proposedAnswers}
                nextRoleVisibility={nextRoleVisibility}
                onChangeNextRoleVisibility={(sectionId, show) =>
                  setNextRoleVisibility((previous) => ({
                    ...previous,
                    [sectionId]: show,
                  }))
                }
                onChangeProposedAnswers={setProposedAnswers}
              />
            </div>
          )}
          {!assessment && (
            <div className="mt-0 flex justify-center">
              {label(assessmentLabel, { capitalize: true })} not found
            </div>
          )}
        </Layout.Main>

        {!isEngagementSurvey &&
          assessment?.id &&
          assessment.complianceProgram &&
          !isInSidebar &&
          target && (
            <AssessmentLayoutSidebar
              target={target}
              periodStartDate={assessment.periodStartDate}
              periodEndDate={assessment.periodEndDate}
              relatedMeetings={relatedMeetings}
              isManager={!!isManager}
              key={assessmentId}
              sidebarExpandedUiPreferenceKey="assessmentSidebarExpanded"
              assessmentId={assessmentId}
              complianceProgramId={assessment.complianceProgram.id}
              aiToolReview={aiToolReview}
              pageContextMessage={pageContextMessage}
            />
          )}
      </Layout.Container>
    </Layout>
  );
};

export default Assessment;
